/* eslint-disable no-nested-ternary */
import {
  ActionIcon,
  Affix,
  Button,
  Center,
  Indicator,
  Space,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { CSSProperties, FC, MouseEvent as ReactMouseEvent, ReactNode, useContext, useEffect, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import {
  IconAddressBook,
  IconCalendarEvent,
  IconChartBar,
  IconCircles,
  IconFileDescription,
  IconListCheck,
  IconLogout,
  IconMailbox,
  IconSchool,
  IconSettings,
  IconSwitchHorizontal,
  TablerIcon,
} from '@tabler/icons';
import { closeModal, openModal } from '@mantine/modals';
import { AuthContext } from '../Service/Context/AuthContext';
import { ProfileContext } from '../Service/Context/ProfileContext';
import { RoutingS } from '../Service/RoutingS';
import { TestS } from '../TestS';
import { ConstantS } from '../Service/ConstantS';
import { MixpanelS } from '../Service/MixpanelS';
import { LicenceS } from '../Service/LicenceS';
import { EventS } from '../Service/EventS';
import ThemeSettingsIcon from '../Atoms/icons/ThemeSettingsIcon';
import { ReactComponent as IconBookitupCircle } from './bookitup-signet.svg';
import NotificationNavIcon from './Notifications/NotificationNavIcon';

type NavigationLink = {
  icon: TablerIcon;
  disabled?: boolean;
  label: string;
  internalLabel?: string;
  path: string;
  href?: string;
  hoverColor?: string;
  onClick?: () => void;
  hidden?: boolean;
  testId?: string;
};

type PrimaryNavLinkProps = Pick<NavigationLink, 'icon' | 'disabled' | 'label' | 'internalLabel' | 'path' | 'testId'> & {
  children?: (actionIcon: ReactNode) => ReactNode;
};

const PrimaryNavLink: FC<PrimaryNavLinkProps> = ({
  icon: Icon,
  disabled,
  label,
  internalLabel,
  path,
  testId,
  children,
}) => {
  const { pathname } = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();
  const darkMode = colorScheme === 'dark';
  const active = path === '/' ? path === pathname || pathname.startsWith('/events') : pathname.startsWith(path);
  const iconStyle: CSSProperties = darkMode
    ? { color: active ? colors.blue[1] : '#E5E5E5' }
    : { color: active ? colors.blue[4] : '#1A1A1A' };

  // eslint-disable-next-line no-underscore-dangle
  const _onClick = () => {
    navigate(path);
    if (internalLabel) {
      MixpanelS.track(ConstantS.TrackingEvents.ViewContent, { page: internalLabel });
    }
  };

  const onAnchorClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      openForceEventCreationModal();
      return;
    }

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    _onClick();
  };

  const actionIcon = (
    <ActionIcon
      sx={(theme) => ({
        backgroundColor: active ? (darkMode ? theme.colors.dark[6] : theme.colors.lightBlue[0]) : 'transparent',
        width: 40,
        height: 40,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 10,
      })}
      data-testid={testId}
    >
      <Icon style={iconStyle} />
    </ActionIcon>
  );

  return (
    <Tooltip label={label} position="right" withArrow transition="slide-right">
      <a
        href={disabled ? undefined : path}
        aria-disabled={disabled}
        onClick={onAnchorClick}
        style={{ textDecoration: 'none' }}
      >
        {children ? children(actionIcon) : actionIcon}
      </a>
    </Tooltip>
  );
};

const SecondaryNavLink: FC<{ link: NavigationLink }> = ({ link }) => {
  const { hoverColor, path, href, onClick, hidden, testId, disabled, label } = link;
  const { pathname } = useLocation();
  const [hover, setHover] = useState<boolean>(false);
  const navigate: NavigateFunction = useNavigate();
  const active = pathname.includes(path);
  const { colors, colorScheme } = useMantineTheme();
  // eslint-disable-next-line no-nested-ternary
  const itemColor = hover ? hoverColor : active ? colors.blue[colorScheme === 'dark' ? 1 : 4] : 'gray';
  const hrefPath = href || path;
  const linkSupportsNewTab = hrefPath.startsWith('/');

  if (hidden) {
    return null;
  }

  const onActionClick = () => {
    if (!disabled) {
      if (onClick) {
        onClick();
      } else {
        navigate(link.path);
      }
    } else {
      openForceEventCreationModal();
    }
  };

  const onAnchorClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      openForceEventCreationModal();
      return;
    }

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    onActionClick();
  };

  return (
    <Tooltip label={label} position="right" withArrow transition="slide-right" key={label}>
      {linkSupportsNewTab ? (
        <a
          href={disabled ? undefined : hrefPath}
          aria-disabled={disabled}
          onClick={onAnchorClick}
          style={{ textDecoration: 'none' }}
        >
          <ActionIcon
            data-testid={testId}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 10,
              color: itemColor,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <link.icon />
          </ActionIcon>
        </a>
      ) : (
        <ActionIcon
          data-testid={testId}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={onActionClick}
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 10,
            color: itemColor,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <link.icon />
        </ActionIcon>
      )}
    </Tooltip>
  );
};

const PrimaryNavbar: FC = () => {
  const { logout } = useContext(AuthContext);
  const navigate: NavigateFunction = useNavigate();
  const { hasSwitchableProfiles, switchProfile, loadingProfiles, profile, overdueTasks } = useContext(ProfileContext);
  const [futureEventCount, setFutureEventCount] = useState(0);

  useEffect(() => {
    EventS.fetchFutureEventsCount().then(setFutureEventCount);
  }, []);

  const forceEventView = false; // totalCount === 0 && profile && profile.id % 2 === 1;

  const secondaryNavigationLinks = [
    {
      icon: IconSwitchHorizontal,
      label: 'Profil wechseln',
      path: 'foo',
      onClick: switchProfile,
      hidden: loadingProfiles || !hasSwitchableProfiles,
    },
    {
      icon: IconSettings,
      label: 'Einstellungen',
      path: '/settings',
      href: '/settings/userdata',
      onClick: () => {
        navigate('/settings/userdata');
        MixpanelS.track(ConstantS.TrackingEvents.ViewContent, { page: 'Settings' });
      },
      testId: TestS.Navigate.TO_SETTINGS,
      disabled: forceEventView,
    },
    {
      icon: IconSchool,
      label: 'Akademie',
      path: '/academy',
      href: '/academy/welcome',
      onClick: () => navigate('/academy/welcome'),
    },
    {
      icon: IconLogout,
      label: 'Abmelden',
      path: '/logout',
      hoverColor: 'red',
      onClick: () => {
        MixpanelS.track(ConstantS.TrackingEvents.Logout);
        logout();
      },
    },
  ];

  return (
    <>
      <Stack spacing={10} p="sm" sx={{ width: 60 }}>
        <Center>
          <IconBookitupCircle
            height={33}
            width={33}
            onClick={() => navigate(RoutingS.ROUTES.HOMEPAGE)}
            style={{ cursor: 'pointer' }}
          />
        </Center>
        <Space h={2} />
        <NotificationNavIcon />
        <PrimaryNavLink
          icon={IconCalendarEvent}
          label="Events"
          internalLabel="Events"
          path="/"
          testId={TestS.Navigate.TO_EVENTS}
          disabled={false}
        >
          {(actionIcon) => (
            <Indicator
              inline
              disabled={futureEventCount < 1}
              radius="xl"
              offset={7}
              size={20}
              color="red"
              label={
                <Text color="white" size={10}>
                  {futureEventCount}
                </Text>
              }
            >
              {actionIcon}
            </Indicator>
          )}
        </PrimaryNavLink>
        {profile && LicenceS.hasAccessToResourceModule(profile.id) && (
          <PrimaryNavLink
            icon={IconCircles}
            label="Ressourcen"
            internalLabel="Resources"
            path="/resources/all"
            testId={TestS.Navigate.TO_RESOURCES}
            disabled={forceEventView}
          />
        )}
        <PrimaryNavLink
          icon={IconListCheck}
          label="Aufgaben"
          internalLabel="Tasks"
          path="/tasks"
          testId={TestS.Navigate.TO_TASKS}
          disabled={forceEventView}
        >
          {(actionIcon) => (
            <Indicator
              inline
              disabled={overdueTasks.count < 1}
              radius="xl"
              offset={7}
              size={20}
              color="red"
              label={
                <Text color="white" size={10}>
                  {overdueTasks.count}
                </Text>
              }
            >
              {actionIcon}
            </Indicator>
          )}
        </PrimaryNavLink>
        <PrimaryNavLink
          icon={IconMailbox}
          label="Postfach"
          internalLabel="Inbox"
          path="/postbox/inbox"
          testId={TestS.Navigate.TO_MAILS}
          disabled={forceEventView}
        />
        <PrimaryNavLink
          icon={IconAddressBook}
          label="Kontakte"
          internalLabel="Contacts"
          path="/contacts"
          testId={TestS.Navigate.TO_CONTACTS}
          disabled={forceEventView}
        />
        <PrimaryNavLink
          icon={IconFileDescription}
          label="Dokumente"
          internalLabel="Documents"
          path="/documents"
          testId={TestS.Navigate.TO_DOCUMENTS}
          disabled={forceEventView}
        />
        <PrimaryNavLink
          icon={IconChartBar}
          label="Statistiken"
          internalLabel="Statistics"
          path={`/statistics?year=${new Date().getFullYear()}`}
          testId={TestS.Navigate.TO_STATISTICS}
          disabled={forceEventView}
        />
      </Stack>
      <Affix position={{ bottom: 30, left: 12 }}>
        <Stack>
          <ThemeSettingsIcon />
          {secondaryNavigationLinks.map((link) => (
            <SecondaryNavLink key={link.label} link={link} />
          ))}
        </Stack>
      </Affix>
    </>
  );
};

export const openForceEventCreationModal = () =>
  openModal({
    modalId: 'force-event-creation-modal',
    title: (
      <Text weight="bolder" size="xl" align="center">
        ⚠️ Noch kein Event erstellt
      </Text>
    ),
    children: (
      <Text size="sm">
        Um diesen Bereich freizuschalten, erstelle bitte zuerst ein Event.
        <Text size="xs" color="dimmed" sx={{ marginTop: 10 }}>
          Klicke dafür auf das blaue Plus, wähle ein Event-Datum aus und gib einen Kundennamen an.
        </Text>
        <Center>
          <Button mt="xl" onClick={() => closeModal('force-event-creation-modal')} sx={{ width: 250 }}>
            Alles klar!
          </Button>
        </Center>
      </Text>
    ),
  });

export default PrimaryNavbar;
